import React, { useState, useEffect } from 'react';
import { gameplayerAPI, playertoolsAPI } from '../../services/api';

const POSITION_TYPES = [
  { value: 'GK', label: 'GK – Goalkeeper' },
  { value: 'CB', label: 'CB – Centre Back' },
  { value: 'LB', label: 'LB – Left Back' },
  { value: 'RB', label: 'RB – Right Back' },
  { value: 'DM', label: 'DM – Defensive Mid' },
  { value: 'CM', label: 'CM – Central Mid' },
  { value: 'AM', label: 'AM – Attacking Mid' },
  { value: 'LW', label: 'LW – Left Wing' },
  { value: 'RW', label: 'RW – Right Wing' },
  { value: 'ST', label: 'ST – Striker' },
];

const PlayerRow = ({ entry, onRemove }) => (
  <div className="flex items-center justify-between rounded-lg px-3 py-2 bg-white border border-gray-100">
    <div className="flex items-center gap-2">
      <span className="text-xs font-bold text-gray-400 w-8 text-center">{entry.field || '—'}</span>
      <span className="text-sm font-medium text-gray-800">
        {entry.plid?.surname} {entry.plid?.prename}
      </span>
      {entry.type === 1 && (
        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Sub</span>
      )}
    </div>
    <button
      className="btn btn-xs btn-ghost text-red-500 hover:text-red-700 btn-square"
      onClick={() => onRemove(entry._id)}
      title="Remove from lineup"
    >
      ✕
    </button>
  </div>
);

const toStr = (v) => (v?._id || v || '').toString();

const AddPlayerForm = ({ teamId, teamName, playerAssignments, onAdd, onCancel }) => {
  const [plid, setPlid] = useState('');
  const [field, setField] = useState('GK'); // must not be empty (API validation)
  const [type, setType] = useState('0');
  const [minute, setMinute] = useState('0');

  // Compare as strings to handle ObjectId objects vs plain strings
  const eligible = playerAssignments.filter(a => toStr(a.tid) === toStr(teamId));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!plid) return;
    // field and minute must be non-empty strings (backend validation)
    onAdd({ plid, field: field || 'N/A', type: parseInt(type), minute: minute || '0' });
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3 p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Add player – {teamName}</p>
      <div className="grid grid-cols-2 gap-2">
        <div className="form-control col-span-2">
          <label className="label"><span className="label-text">Player *</span></label>
          <select required className="select select-bordered" value={plid} onChange={e => setPlid(e.target.value)}>
            <option value="">Select player…</option>
            {eligible.map(a => (
              <option key={a._id} value={toStr(a.plid)}>
                #{a.num} {a.plid?.surname} {a.plid?.prename}
              </option>
            ))}
          </select>
          {eligible.length === 0 && (
            <span className="text-xs text-amber-600 mt-1">
              No players assigned to this team in this project yet. Assign players first via Settings → Player Assignments.
            </span>
          )}
        </div>
        <div className="form-control">
          <label className="label"><span className="label-text">Position *</span></label>
          <select required className="select select-bordered" value={field} onChange={e => setField(e.target.value)}>
            {POSITION_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div className="form-control">
          <label className="label"><span className="label-text">Role</span></label>
          <select className="select select-bordered" value={type} onChange={e => setType(e.target.value)}>
            <option value="0">Starter</option>
            <option value="1">Substitute</option>
          </select>
        </div>
        {parseInt(type) === 1 && (
          <div className="form-control">
            <label className="label"><span className="label-text">Minute in</span></label>
            <input type="number" min="0" max="120" className="input input-bordered" value={minute} onChange={e => setMinute(e.target.value)} />
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <button type="submit" className="btn btn-primary btn-sm" disabled={!plid}>Add to Lineup</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

const GameRoster = ({ game, onClose }) => {
  const [lineup, setLineup] = useState([]);
  const [playerAssignments, setPlayerAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addingFor, setAddingFor] = useState(null); // null | 'home' | 'away'

  const pidValue = game.pid?._id || game.pid;
  const homeId = game.hid?._id || game.hid;
  const awayId = game.aid?._id || game.aid;

  useEffect(() => {
    fetchLineup();
    fetchPlayerAssignments();
  }, [game._id]);

  const fetchLineup = async () => {
    try {
      const res = await gameplayerAPI.getByGame(game._id);
      setLineup(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError('Could not load lineup.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerAssignments = async () => {
    try {
      const res = await playertoolsAPI.getAll({ pid: pidValue });
      const data = res.data?.playertools || res.data || [];
      setPlayerAssignments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Could not fetch player assignments', err);
    }
  };

  const handleAdd = async ({ plid, field, type, minute }) => {
    const tid = addingFor === 'home' ? homeId : awayId;
    try {
      await gameplayerAPI.create({
        pid: pidValue,
        gid: game._id,
        tid,
        plid,
        field: field || 'N/A',   // must not be empty (backend validation)
        type,
        minute: String(minute ?? '0'), // must not be empty (backend validation)
      });
      setAddingFor(null);
      setError('');
      fetchLineup();
    } catch (err) {
      const msg = err.response?.data?.message
        || (Array.isArray(err.response?.data?.errors) ? err.response.data.errors.map(e => e.msg).join(', ') : null)
        || 'Failed to add player to lineup.';
      setError(msg);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this player from the lineup?')) return;
    try {
      await gameplayerAPI.delete(id);
      fetchLineup();
    } catch (err) {
      setError('Failed to remove player.');
    }
  };

  const starters = (tid) => lineup.filter(p => toStr(p.tid) === toStr(tid) && p.type !== 1);
  const subs = (tid) => lineup.filter(p => toStr(p.tid) === toStr(tid) && p.type === 1);

  const TeamColumn = ({ teamId, teamName, side }) => (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className={`font-semibold text-sm ${side === 'home' ? 'text-blue-700' : 'text-purple-700'}`}>
          {teamName}
          <span className="ml-1 text-xs font-normal text-gray-400">({side === 'home' ? 'Home' : 'Away'})</span>
        </h4>
        <button
          className={`btn btn-xs ${side === 'home' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setAddingFor(addingFor === side ? null : side)}
        >
          {addingFor === side ? 'Cancel' : '+ Add'}
        </button>
      </div>

      {addingFor === side && (
        <AddPlayerForm
          teamId={teamId}
          teamName={teamName}
          playerAssignments={playerAssignments}
          onAdd={handleAdd}
          onCancel={() => setAddingFor(null)}
        />
      )}

      <div className="mt-2 space-y-1.5">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Starters ({starters(teamId).length})</p>
        {starters(teamId).length === 0
          ? <p className="text-xs text-gray-400 italic">None added</p>
          : starters(teamId).map(e => <PlayerRow key={e._id} entry={e} onRemove={handleRemove} />)
        }
        {subs(teamId).length > 0 && (
          <>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mt-3">Substitutes ({subs(teamId).length})</p>
            {subs(teamId).map(e => <PlayerRow key={e._id} entry={e} onRemove={handleRemove} />)}
          </>
        )}
      </div>
    </div>
  );

  return (
    <dialog open className="modal">
      <div className="modal-box w-11/12 max-w-3xl">
        <h3 className="font-bold text-lg">
          Lineup — {game.hid?.name} vs {game.aid?.name}
        </h3>

        {error && (
          <div className="alert alert-error mt-3 text-sm py-2">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-10">
            <span className="loading loading-spinner loading-md"></span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <TeamColumn teamId={homeId} teamName={game.hid?.name} side="home" />
            <TeamColumn teamId={awayId} teamName={game.aid?.name} side="away" />
          </div>
        )}

        <div className="modal-action">
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </dialog>
  );
};

export default GameRoster;
